import type { GtmEcommerceItem, GtmPurchasePayload } from '~/utils/gtmEcommerce'

export function metaPurchaseEventId(orderNumber: string): string {
  return `purchase-${orderNumber}`
}

export function getMetaCookie(name: '_fbp' | '_fbc'): string | undefined {
  if (!import.meta.client) return undefined
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : undefined
}

export function buildMetaContents(items: GtmEcommerceItem[]) {
  return items.map((item) => ({
    id: item.item_id,
    quantity: item.quantity,
    item_price: item.price,
  }))
}

export function buildMetaPurchaseParams(payload: GtmPurchasePayload): Record<string, unknown> {
  const contents = buildMetaContents(payload.items)
  return {
    value: payload.value,
    currency: 'RON',
    content_type: 'product',
    content_ids: contents.map((entry) => entry.id),
    contents,
    num_items: payload.items.reduce((sum, item) => sum + item.quantity, 0),
    order_id: payload.transaction_id,
    shipping: payload.shipping ?? 0,
    tax: payload.tax ?? 0,
    coupon: payload.coupon || undefined,
  }
}

export function buildMetaViewContentParams(
  item: GtmEcommerceItem,
): Record<string, unknown> {
  return {
    value: item.price,
    currency: 'RON',
    content_type: 'product',
    content_ids: [item.item_id],
    contents: [{ id: item.item_id, quantity: item.quantity, item_price: item.price }],
  }
}

export function buildMetaAddToCartParams(
  item: GtmEcommerceItem,
): Record<string, unknown> {
  return {
    value: item.price * item.quantity,
    currency: 'RON',
    content_type: 'product',
    content_ids: [item.item_id],
    contents: [{ id: item.item_id, quantity: item.quantity, item_price: item.price }],
  }
}

export function buildMetaInitiateCheckoutParams(
  items: GtmEcommerceItem[],
  value: number,
): Record<string, unknown> {
  const contents = buildMetaContents(items)
  return {
    value,
    currency: 'RON',
    content_type: 'product',
    content_ids: contents.map((entry) => entry.id),
    contents,
    num_items: items.reduce((sum, item) => sum + item.quantity, 0),
  }
}

export function isOrderPurchaseTrackable(
  order: { payments?: Array<{ method?: string; status?: string }>; payment?: { method?: string; status?: string } } | null,
  stripePaid?: boolean,
): boolean {
  if (stripePaid) return true
  if (!order) return true

  const payment = order.payments?.[0] || order.payment
  if (!payment) return true
  if (payment.method === 'cash_on_delivery') return true
  return payment.status === 'completed'
}
